/**
 * Single source of truth for Clerk roles and permissions.
 *
 * If you update roles or permissions in the Clerk dashboard,
 * mirror those changes here so the frontend stays in sync.
 *
 * Custom roles (per-merchant) are stored in Clerk org public_metadata.customRoles.
 * Their permission key format is "custom:<slug>" (e.g. "custom:volunteer_coordinator").
 * Users with a custom role have org:member as their Clerk native role.
 */

// ---------------------------------------------------------------------------
// Roles
// ---------------------------------------------------------------------------

export const ROLES = {
  ADMIN: 'org:admin',
  FINANCE: 'org:finance',
  EDITOR: 'org:editor',
  MARKETING: 'org:marketing',
  MEMBER: 'org:member', // Base role for custom per-merchant roles
};

export const ROLE_LABELS = {
  [ROLES.ADMIN]: 'Admin',
  [ROLES.FINANCE]: 'Finance',
  [ROLES.EDITOR]: 'Editor',
  [ROLES.MARKETING]: 'Marketing',
  // CON-228: a plain member (no custom_role) now has a default permission set,
  // so it gets a label wherever a role key is shown. The permissions page lists
  // CUSTOMIZABLE_ROLES plus the org's custom roles, never ROLE_PERMISSIONS keys,
  // so this does not add an org:member column there.
  [ROLES.MEMBER]: 'Member',
};

// Shared descriptors for every surface that assigns or explains a built-in
// role. `value` is the CharityStack API value; `role` is the Clerk native role.
export const BUILTIN_ROLE_OPTIONS = [
  {
    value: 'ADMIN',
    role: ROLES.ADMIN,
    label: 'Admin',
    summary: 'Full access to all features',
    description:
      'Admins have full access to all features including settings, user management, payments, forms, and elements.',
    badgeClass: 'bg-status-plum-bg text-status-plum-text',
    checkboxClass: 'text-status-plum-text focus:ring-status-plum-text',
  },
  {
    value: 'FINANCE',
    role: ROLES.FINANCE,
    label: 'Finance',
    summary: 'Payments and payouts',
    description:
      'Finance users can manage payments and view payouts. They cannot manage forms, elements, or settings.',
    badgeClass: 'bg-status-blue-bg text-status-blue-text',
    checkboxClass: 'text-status-blue-text focus:ring-status-blue-text',
  },
  {
    value: 'EDITOR',
    role: ROLES.EDITOR,
    label: 'Editor',
    summary: 'Forms and elements only',
    description:
      'Editors can create and manage forms and elements. They cannot access payments, subscriptions, payouts, or settings.',
    badgeClass: 'bg-sage-light text-sage',
    checkboxClass: 'text-sage focus:ring-sage',
  },
  {
    value: 'MARKETING',
    role: ROLES.MARKETING,
    label: 'Marketing',
    summary: 'Contacts and marketing',
    description:
      'Marketing users can view and manage contacts and create, view, and edit broadcasts and segments. They cannot create or import contacts or access payments, payouts, subscriptions, forms, elements, billing, or user administration.',
    badgeClass: 'bg-status-amber-bg text-status-amber-text',
    checkboxClass: 'text-status-amber-text focus:ring-status-amber-text',
  },
];

// Built-in roles that can have their permissions customized per-organization
export const CUSTOMIZABLE_ROLES = [ROLES.FINANCE, ROLES.EDITOR, ROLES.MARKETING];

// Maximum custom roles per org (enforced backend, shown in UI)
export const MAX_CUSTOM_ROLES = 5;

// ---------------------------------------------------------------------------
// Permissions  (match the Clerk dashboard exactly)
// ---------------------------------------------------------------------------

export const P = {
  // Payments
  MANAGE_PAYMENTS: 'org:payments:manage_payments',
  PROCESS_PAYMENTS: 'org:payments:process_payments',
  VIEW_PAYMENTS: 'org:payments:view_payments',

  // Elements
  MANAGE_ELEMENTS: 'org:elements:manage_elements',
  PROCESS_ELEMENTS: 'org:elements:process_elements',
  VIEW_ELEMENTS: 'org:elements:view_elements',

  // Forms
  MANAGE_FORMS: 'org:forms:manage_forms',
  PROCESS_FORMS: 'org:forms:process_forms',
  VIEW_FORMS: 'org:forms:view_forms',

  // Payouts
  MANAGE_PAYOUTS: 'org:payouts:manage_payouts',
  VIEW_PAYOUTS: 'org:payouts:view_payouts',

  // Billing
  MANAGE_BILLING: 'org:billing:manage_billing',

  // Settings
  MANAGE_CHECKOUT_SETTINGS: 'org:settings:manage_checkout_settings',
  MANAGE_EMAIL_SETTINGS: 'org:settings:manage_email_settings',
  MANAGE_INTEGRATION_SETTINGS: 'org:settings:manage_integration_settings',
  MANAGE_ORGANIZATION_SETTINGS: 'org:settings:manage_organization_settings',
  MANAGE_PROCESSOR_SETTINGS: 'org:settings:manage_processor_settings',
  // Portal settings (PE-1846 — Settings > Portal customization + widget).
  // ⚠️ Backend counterpart required before orgs can grant this to non-admin
  // roles: accounts-backend functions/permissions.py ALL_PERMISSIONS must
  // include this key (the save endpoint 400s unknown permissions). Admins
  // resolve from the frontend ADMIN defaults, so admin access works now.
  MANAGE_PORTAL_SETTINGS: 'org:settings:manage_portal_settings',

  // Contacts
  MANAGE_CONTACTS: 'org:contacts:manage_contacts',
  PROCESS_CONTACTS: 'org:contacts:process_contacts',
  VIEW_CONTACTS: 'org:contacts:view_contacts',
  // Dashboard Import Tool (PE-1584, SPEC §2.5). import_contacts covers
  // start/map/validate/commit + inline custom-field creation during mapping;
  // reverse_imports is destructive (deletes the contacts an import created).
  // ⚠️ Backend counterpart required before orgs can grant either to a
  // non-default role: accounts-backend's allowed-permissions list must
  // accept these keys or the role-editor save endpoint 400s (the same
  // PE-1846 gotcha as MANAGE_PORTAL_SETTINGS above).
  IMPORT_CONTACTS: 'org:contacts:import_contacts',
  REVERSE_IMPORTS: 'org:contacts:reverse_imports',

  // Subscriptions
  MANAGE_SUBSCRIPTIONS: 'org:subscriptions:manage_subscriptions',
  VIEW_SUBSCRIPTIONS: 'org:subscriptions:view_subscriptions',

  // Marketing settings (PocketFlows initiative — the Marketing tab in
  // Settings: provisioning, sender email, custom domain, SMS number).
  MANAGE_MARKETING: 'org:marketing:manage_marketing',

  // Broadcasts (PocketFlows initiative — campaigns + triggers; Email
  // Blasts share these permissions).
  CREATE_BROADCASTS: 'org:broadcasts:create_broadcasts',
  VIEW_BROADCASTS: 'org:broadcasts:view_broadcasts',
  EDIT_BROADCASTS: 'org:broadcasts:edit_broadcasts',

  // Segments (PocketFlows initiative).
  CREATE_SEGMENTS: 'org:segments:create_segments',
  VIEW_SEGMENTS: 'org:segments:view_segments',
  EDIT_SEGMENTS: 'org:segments:edit_segments',

  // Events (CON-228 — Mobile POS check-in). Checked per request by
  // PATCH /tickets once CON-252 lands.
  // ⚠️ Backend counterpart required: accounts-backend functions/permissions.py
  // ALL_PERMISSIONS must include this key and deploy BEFORE this dashboard
  // change, or every role save that carries it 400s (the PE-1846 gotcha).
  CHECK_IN_ATTENDEES: 'org:events:check_in_attendees',
};

// The "does this user have access to ANY Marketing surface" check used by
// the marketing tier gate (sidebar section, route registration). A user
// with at least one view permission sees the Marketing section; each tab
// is then gated by its own view permission.
export const MARKETING_VIEW_PERMISSIONS = [P.VIEW_BROADCASTS, P.VIEW_SEGMENTS];

// ---------------------------------------------------------------------------
// Permission UI metadata  (for the PermissionsManagement component)
// ---------------------------------------------------------------------------

export const PERMISSION_GROUPS = [
  {
    label: 'Payments',
    permissions: [P.PROCESS_PAYMENTS, P.VIEW_PAYMENTS, P.MANAGE_PAYMENTS],
  },
  {
    label: 'Contacts',
    permissions: [
      P.PROCESS_CONTACTS,
      P.VIEW_CONTACTS,
      P.MANAGE_CONTACTS,
      P.IMPORT_CONTACTS,
      P.REVERSE_IMPORTS,
    ],
  },
  {
    label: 'Subscriptions',
    permissions: [P.VIEW_SUBSCRIPTIONS, P.MANAGE_SUBSCRIPTIONS],
  },
  {
    label: 'Forms',
    permissions: [P.PROCESS_FORMS, P.VIEW_FORMS, P.MANAGE_FORMS],
  },
  {
    label: 'Elements',
    permissions: [P.PROCESS_ELEMENTS, P.VIEW_ELEMENTS, P.MANAGE_ELEMENTS],
  },
  {
    label: 'Payouts',
    permissions: [P.VIEW_PAYOUTS, P.MANAGE_PAYOUTS],
  },
  {
    label: 'Settings',
    permissions: [
      P.MANAGE_ORGANIZATION_SETTINGS,
      P.MANAGE_CHECKOUT_SETTINGS,
      P.MANAGE_PORTAL_SETTINGS,
      P.MANAGE_EMAIL_SETTINGS,
      P.MANAGE_INTEGRATION_SETTINGS,
      P.MANAGE_PROCESSOR_SETTINGS,
      P.MANAGE_BILLING,
      P.MANAGE_MARKETING,
    ],
    // Per-permission entitlement gates: rows hidden unless the org's plan
    // has the feature (engine key from the entitlements catalog). Portal
    // customization is Base+ — Starter orgs don't see the row at all.
    entitledPermissions: {
      [P.MANAGE_PORTAL_SETTINGS]: 'contact_portal_element',
    },
  },
  // The `pocketflows_marketing_tab` rollout flag that used to hide these
  // groups (and the Manage Marketing row above) from the Role Permissions UI
  // was removed at full rollout (2026-08).
  {
    label: 'Broadcasts',
    permissions: [P.CREATE_BROADCASTS, P.VIEW_BROADCASTS, P.EDIT_BROADCASTS],
  },
  {
    label: 'Segments',
    permissions: [P.CREATE_SEGMENTS, P.VIEW_SEGMENTS, P.EDIT_SEGMENTS],
  },
  {
    label: 'Events',
    permissions: [P.CHECK_IN_ATTENDEES],
  },
  // Email Templates permissions fully retired in PE-1935 (the UI surface was
  // removed earlier in PE-1671). The P.*_EMAIL_TEMPLATES constants + their
  // role-matrix assignments are gone from this catalog; the separate
  // `email_templates` plan entitlement is unaffected.
];

export const PERMISSION_LABELS = {
  // CON-228 plain-language wording (approved 2026-10-07). view_contacts is
  // dashboard-only on purpose: the POS app does not gate donor search on it.
  [P.PROCESS_PAYMENTS]: 'Take payments in the POS app and the dashboard',
  [P.VIEW_PAYMENTS]: "View payments, form's drilldowns, and exports",
  [P.MANAGE_PAYMENTS]: 'Edit and delete payments',
  [P.PROCESS_CONTACTS]: 'Create contacts',
  [P.VIEW_CONTACTS]: 'Search donors in dashboard',
  [P.MANAGE_CONTACTS]: 'Edit and merge contacts',
  [P.IMPORT_CONTACTS]: 'Import contacts from a file',
  [P.REVERSE_IMPORTS]: 'Reverse a completed import (deletes imported contacts)',
  [P.VIEW_SUBSCRIPTIONS]: 'View subscriptions',
  [P.MANAGE_SUBSCRIPTIONS]: 'Edit and cancel subscriptions',
  [P.PROCESS_FORMS]: 'Create forms',
  [P.VIEW_FORMS]: 'View forms',
  [P.MANAGE_FORMS]: 'Edit forms',
  [P.PROCESS_ELEMENTS]: 'Create elements',
  [P.VIEW_ELEMENTS]: 'View elements',
  [P.MANAGE_ELEMENTS]: 'Edit elements',
  [P.VIEW_PAYOUTS]: 'View payouts and balances drilldowns',
  [P.MANAGE_PAYOUTS]: 'Manage payout frequency',
  [P.MANAGE_BILLING]: 'Manage billing plan',
  [P.MANAGE_ORGANIZATION_SETTINGS]: 'Update organization info',
  [P.MANAGE_CHECKOUT_SETTINGS]: 'Edit global checkout FAQs and custom input fields',
  [P.MANAGE_PORTAL_SETTINGS]: 'Customize the donor portal and widget',
  [P.MANAGE_EMAIL_SETTINGS]: 'Edit transactional and end-of-year emails',
  [P.MANAGE_INTEGRATION_SETTINGS]: 'Connect or disconnect integrations',
  [P.MANAGE_PROCESSOR_SETTINGS]: 'Manage payment processor and compliance',
  [P.MANAGE_MARKETING]: 'Manage marketing settings (sender email, domain, SMS)',
  [P.CREATE_BROADCASTS]: 'Create and duplicate broadcasts',
  [P.VIEW_BROADCASTS]: 'View broadcasts',
  [P.EDIT_BROADCASTS]: 'Edit, start/stop, and delete broadcasts',
  [P.CREATE_SEGMENTS]: 'Create and duplicate segments',
  [P.VIEW_SEGMENTS]: 'View segments',
  [P.EDIT_SEGMENTS]: 'Edit and delete segments',
  [P.CHECK_IN_ATTENDEES]: 'Check guests in at events',
};

// ---------------------------------------------------------------------------
// Role → Permission mapping  (mirrors Clerk dashboard configuration)
// ---------------------------------------------------------------------------

export const ROLE_PERMISSIONS = {
  [ROLES.ADMIN]: [
    P.MANAGE_CONTACTS,
    P.PROCESS_CONTACTS,
    P.VIEW_CONTACTS,
    // PE-1584 — Dashboard Import Tool (SPEC §2.5, resolved 2026-07-30).
    P.IMPORT_CONTACTS,
    P.REVERSE_IMPORTS,
    P.MANAGE_PAYMENTS,
    P.PROCESS_PAYMENTS,
    P.VIEW_PAYMENTS,
    P.MANAGE_ELEMENTS,
    P.PROCESS_ELEMENTS,
    P.VIEW_ELEMENTS,
    P.MANAGE_FORMS,
    P.PROCESS_FORMS,
    P.VIEW_FORMS,
    P.MANAGE_BILLING,
    P.MANAGE_PAYOUTS,
    P.VIEW_PAYOUTS,
    P.MANAGE_CHECKOUT_SETTINGS,
    P.MANAGE_PORTAL_SETTINGS,
    P.MANAGE_EMAIL_SETTINGS,
    P.MANAGE_INTEGRATION_SETTINGS,
    P.MANAGE_ORGANIZATION_SETTINGS,
    P.MANAGE_PROCESSOR_SETTINGS,
    P.MANAGE_SUBSCRIPTIONS,
    P.VIEW_SUBSCRIPTIONS,
    P.MANAGE_MARKETING,
    P.CREATE_BROADCASTS,
    P.VIEW_BROADCASTS,
    P.EDIT_BROADCASTS,
    P.CREATE_SEGMENTS,
    P.VIEW_SEGMENTS,
    P.EDIT_SEGMENTS,
    P.CHECK_IN_ATTENDEES,
  ],

  [ROLES.FINANCE]: [
    P.MANAGE_CONTACTS,
    P.PROCESS_CONTACTS,
    P.VIEW_CONTACTS,
    P.MANAGE_PAYMENTS,
    P.PROCESS_PAYMENTS,
    P.VIEW_PAYMENTS,
    P.VIEW_PAYOUTS,
    P.MANAGE_SUBSCRIPTIONS,
    P.VIEW_SUBSCRIPTIONS,
    // Marketing: view-only (no create/edit, no tags/settings management)
    P.VIEW_BROADCASTS,
    P.VIEW_SEGMENTS,
    // CON-228: every non-read-only role keeps check-in once PATCH /tickets
    // checks this key (CON-252).
    P.CHECK_IN_ATTENDEES,
  ],

  [ROLES.EDITOR]: [
    P.MANAGE_ELEMENTS,
    P.PROCESS_ELEMENTS,
    P.VIEW_ELEMENTS,
    P.MANAGE_FORMS,
    P.PROCESS_FORMS,
    P.VIEW_FORMS,
    // PE-1584 — Dashboard Import Tool (SPEC §2.5, resolved 2026-07-30). Editor
    // previously had NO Contacts permissions at all; VIEW_CONTACTS is added
    // alongside the two import keys so Editor can actually see the Import Hub
    // and its own job's results, not just start a job it can never view.
    // This is a genuine (small) expansion of Editor's scope beyond Import —
    // Editor can now see the Contacts table, which it could not before.
    P.VIEW_CONTACTS,
    P.IMPORT_CONTACTS,
    P.REVERSE_IMPORTS,
    // Marketing: full content access (broadcasts/segments) but not the
    // Settings-level marketing management permission.
    P.CREATE_BROADCASTS,
    P.VIEW_BROADCASTS,
    P.EDIT_BROADCASTS,
    P.CREATE_SEGMENTS,
    P.VIEW_SEGMENTS,
    P.EDIT_SEGMENTS,
    // CON-228: every non-read-only role keeps check-in. Deliberately NO
    // PROCESS_PAYMENTS (Akif 2026-10-11): the Payment Workspace, Create >
    // Payment and the Payments-table button read that same key.
    P.CHECK_IN_ATTENDEES,
  ],

  // PE-2040 — native Marketing role. Contact creation/processing and every
  // import permission are intentionally excluded from the default.
  [ROLES.MARKETING]: [
    P.VIEW_CONTACTS,
    P.MANAGE_CONTACTS,
    P.MANAGE_MARKETING,
    P.CREATE_BROADCASTS,
    P.VIEW_BROADCASTS,
    P.EDIT_BROADCASTS,
    P.CREATE_SEGMENTS,
    P.VIEW_SEGMENTS,
    P.EDIT_SEGMENTS,
    // CON-228: check-in only; no PROCESS_PAYMENTS (see the Editor entry).
    P.CHECK_IN_ATTENDEES,
  ],

  // CON-228: a plain org:member (no custom_role) checks guests in today, so it
  // keeps that, plus taking payments (Akif 2026-10-11: plain members and newly
  // created custom roles take payments; editor and marketing do not). The
  // dashboard's Payment Workspace reads the same key, so a plain member can
  // reach it too (accepted). A member WITH a custom_role resolves as
  // custom:<slug> from the org's list and never reads this entry. Not in CUSTOMIZABLE_ROLES,
  // so the permissions page does not show it as a column.
  [ROLES.MEMBER]: [P.CHECK_IN_ATTENDEES, P.PROCESS_PAYMENTS],
};

// ---------------------------------------------------------------------------
// All permissions (flat list for validation)
// ---------------------------------------------------------------------------

// CON-228 (critic F1): the permissions a NEW custom role starts with. A role
// created with [] would give its members no payments and no check-in. Akif
// 2026-10-11: newly created custom roles take payments and check guests in;
// existing custom roles keep whatever their admin saved.
export const DEFAULT_CUSTOM_ROLE_PERMISSIONS = [P.PROCESS_PAYMENTS, P.CHECK_IN_ATTENDEES];

export const ALL_PERMISSIONS = Object.values(P);

// These permissions are intentionally absent from the active catalog and UI,
// but accounts-backend still accepts them during the expand phase of their
// retirement. Preserve them when an admin saves an unrelated role change so a
// frontend release cannot silently revoke permissions before the backend's
// contract phase is complete.
export const LEGACY_PERMISSIONS_TO_PRESERVE = [
  'org:settings:manage_tags',
  'org:email_templates:create_email_templates',
  'org:email_templates:view_email_templates',
  'org:email_templates:edit_email_templates',
];

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/**
 * Fill only missing built-in role entries from the global defaults. Existing
 * organizations may have saved a replacement map before a new built-in role
 * existed; absence must preserve the product fallback, while an explicit empty
 * array remains a deliberate organization customization.
 *
 * @param {Record<string, string[]> | null | undefined} rolePermissions
 * @returns {Record<string, string[]>}
 */
export function getRolePermissionsWithDefault(rolePermissions, role) {
  const source = rolePermissions && typeof rolePermissions === 'object' ? rolePermissions : {};
  if (Object.prototype.hasOwnProperty.call(source, role)) {
    return Array.isArray(source[role]) ? source[role] : [];
  }
  return ROLE_PERMISSIONS[role] || [];
}

/**
 * Remove truly unknown permissions before saving while retaining transition-
 * era permissions that accounts-backend still supports. Legacy keys stay out
 * of the active catalog, defaults, and UI; this helper only prevents an
 * unrelated save from destructively removing them.
 *
 * @param {Record<string, string[]> | null | undefined} rolePermissions
 * @returns {Record<string, string[]>}
 */
export function sanitizeRolePermissionsForSave(rolePermissions) {
  const savable = new Set([...ALL_PERMISSIONS, ...LEGACY_PERMISSIONS_TO_PRESERVE]);
  return Object.fromEntries(
    Object.entries(rolePermissions || {}).map(([role, permissions]) => [
      role,
      Array.isArray(permissions) ? permissions.filter((permission) => savable.has(permission)) : [],
    ]),
  );
}

/**
 * Resolve effective permissions for a user given their role and optional overrides.
 *
 * Supports built-in roles (including org:marketing) and
 * custom per-merchant roles (custom:<slug>).
 *
 * Evaluation order:
 *   1. Start with org-level custom permissions (if set) or global role defaults
 *   2. Add any user-level "allow" overrides
 *   3. Remove any user-level "deny" overrides  (DENY always wins)
 *
 * Admin always gets all permissions regardless of overrides.
 *
 * @param {string}   role      - Role key (e.g. "org:admin", "org:finance", "custom:volunteer_coordinator")
 * @param {{ allow?: string[], deny?: string[] }} [overrides] - User-level overrides
 * @param {Object}   [orgRolePermissions] - Org-level custom role permissions (e.g. { "org:finance": [...], "custom:volunteer_coordinator": [...] })
 * @returns {string[]} effective permission list
 */
export function resolvePermissions(role, overrides, orgRolePermissions) {
  // Admin always gets everything — not customizable
  if (role === ROLES.ADMIN) {
    return [...ROLE_PERMISSIONS[ROLES.ADMIN]];
  }

  // Step 1: Use org-level custom permissions if available, else global defaults
  // For custom roles (custom:<slug>), orgRolePermissions is the only source
  const basePerms =
    orgRolePermissions && orgRolePermissions[role]
      ? orgRolePermissions[role]
      : ROLE_PERMISSIONS[role] || [];
  const base = new Set(basePerms);

  // Step 2: Apply user-level allow overrides
  if (overrides?.allow) {
    for (const p of overrides.allow) base.add(p);
  }

  // Step 3: Apply user-level deny overrides (DENY always wins)
  if (overrides?.deny) {
    for (const p of overrides.deny) base.delete(p);
  }

  return [...base];
}

/**
 * Check whether a role has a specific permission.
 * @param {string} role   - Clerk role key (e.g. "org:admin")
 * @param {string} permission - Permission key from P (e.g. P.VIEW_PAYMENTS)
 * @returns {boolean}
 */
export function hasPermission(role, permission) {
  const perms = ROLE_PERMISSIONS[role];
  return perms ? perms.includes(permission) : false;
}

/**
 * Return all permissions for a given role.
 * @param {string} role - Clerk role key
 * @returns {string[]}
 */
export function getPermissionsForRole(role) {
  return ROLE_PERMISSIONS[role] || [];
}

/**
 * Get a display label for any role key, including custom roles.
 * @param {string} roleKey - e.g. "org:admin", "custom:volunteer_coordinator"
 * @param {Object} [customRoles] - Custom roles metadata from org (e.g. { "volunteer_coordinator": { label: "..." } })
 * @returns {string}
 */
export function getRoleLabel(roleKey, customRoles) {
  if (ROLE_LABELS[roleKey]) return ROLE_LABELS[roleKey];
  if (roleKey && roleKey.startsWith('custom:')) {
    const slug = roleKey.slice(7); // Remove "custom:" prefix
    return customRoles?.[slug]?.label || slug;
  }
  return roleKey || 'Unknown';
}
